import classnames from 'classnames'
import get from 'lodash/get'
import { FieldProps } from 'formik'
import Icon from 'common/Icon'
import styles from './index.module.scss'

/**
 * @param {boolean} explicitlyShowError show error even if data is prefilled
 * @param {object} field Contains form props like name, value, onChange
 * @param {object} form values, set handlers, get handlers, dirty, isValid, status, etc
 */

type TextInputProps = {
  itemSelected?: boolean
  prependIcon?: () => JSX.Element
  appendIcon?: () => JSX.Element
  onBlur: (e: React.BaseSyntheticEvent) => void
  label?: string
  labelClass?: string
  containerClassName?: string
  inputContainerClassName?: string
  inputClass?: string
  inputErrorClassName?: string
  autoComplete?: string
  hint?: string
  subTextClass?: string
  explicitlyShowError?: boolean
  showNewsletterErrorComponent?: boolean
  errorMessageIcon?: string
  backgroundIcon?: { url: string; style?: React.CSSProperties }
  helperText?: string
}

const TextInput: React.FC<
  FieldProps &
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'form'> &
    TextInputProps
> = (props) => {
  const {
    field,
    form: { touched, errors, setFieldTouched },
    className = '',
    containerClassName = '~mt-6',
    inputContainerClassName = '~flex ~items-center ~justify-between sm:~mb-6 md:~mb-1',
    inputClass,
    inputErrorClassName,
    hint = '',
    prependIcon,
    appendIcon,
    onBlur,
    label = '',
    labelClass = 'fs-14',
    autoComplete = 'none',
    subTextClass = '',
    explicitlyShowError = false,
    showNewsletterErrorComponent = false,
    errorMessageIcon,
    backgroundIcon,
    helperText,
    ...rest
  } = props
  const isTouched = get(touched, field.name)
  const fieldError = get(errors, field.name)
  const showError = (isTouched || explicitlyShowError) && !!fieldError

  const styleControl = backgroundIcon
    ? {
        backgroundImage: `url(${backgroundIcon.url})`,
        backgroundPosition: '10px center',
        backgroundRepeat: 'no-repeat',
        paddingLeft: '35px',
        ...backgroundIcon.style,
      }
    : {}

  const NewsletterErrorDisplay = () => {
    return (
      <div
        className={`~relative ~flex ~flex-col ~min-w-0 ~break-words ~bg-white ~bg-clip-border ~border ~border-[rgba(0,0,0,0.125)] ~rounded-[16px] ~shadow-none ~p-[14px] ${styles.newsletter_error_container}`}
      >
        <div className={styles.icon_container}>
          <Icon icon="exclamationPoint" className={styles.error_icon} />
        </div>
        {field.value?.length < 1 ? (
          <p className={styles.error_message}>
            Oops, this is a mandatory field! Please enter your email.
          </p>
        ) : (
          <p className={styles.error_message}>
            The email address entered is invalid, please check the formatting
            (e.g. email@domain.com).
          </p>
        )}
      </div>
    )
  }

  return (
    <div className={containerClassName}>
      <div className={`${inputContainerClassName} ${subTextClass}`}>
        {!!label && <div className={labelClass}>{label}</div>}
        {!!hint && <div className="microcopy-text">{hint}</div>}
      </div>
      <div onBlur={onBlur} className="~flex ~mb-0 ~relative">
        {!!prependIcon && prependIcon()}
        <input
          style={styleControl}
          autoComplete={autoComplete}
          type="text"
          {...field}
          {...rest}
          onBlur={() => {
            if (!!field.value) {
              setFieldTouched(field.name)
            }
          }}
          className={classnames(
            styles.form_input,
            '~block ~w-full ~py-[0.5rem] ~px-[1rem] ~leading-normal ~text-[#26272b] ~bg-white ~bg-clip-padding ~border-2 ~border-stepper-grey ~rounded-[7px] ~transition-[border-color,box-shadow] ~duration-150 ~ease-in-out ~shadow-none ~min-h-[2.5rem] ~placeholder:font-normal ~placeholder:text-[#8d8f93] ~placeholder:opacity-100 ~focus:bg-[#f4f6fa] ~focus:border-[#082b5f] ~focus:outline-none ~focus:shadow-none ~disabled:bg-[#f7f7f7] ~disabled:text-[#8d8f93] ~disabled:opacity-100 fs-16 font-weight-input',
            `${className} ${inputClass}`,
            {
              'form-control-error': showError && !showNewsletterErrorComponent,
              [styles.newsletterForm_control_error]:
                showError && showNewsletterErrorComponent,
            }
          )}
          aria-label={label}
        />
        {!!appendIcon && appendIcon()}
      </div>
      {helperText && !showError && (
        <div className={classnames(styles.helper_message)}>{helperText}</div>
      )}
      {showError ? (
        showNewsletterErrorComponent ? (
          <NewsletterErrorDisplay />
        ) : (
          <div className={classnames(inputErrorClassName, 'input-error')}>
            {errorMessageIcon}
            {fieldError}
          </div>
        )
      ) : null}
    </div>
  )
}

export default TextInput
